import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { KeyRound, MessageCircle } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, Input, Label } from "@/components/ui-kit";
import { api, errorText } from "@/lib/api";
import { usePrefs } from "@/contexts/PrefsContext";

interface Member { user_id: string; name: string; username: string; venue_role?: string }

const waNum = (p: string) => {
  let d = p.replace(/\D/g, "");
  if (d.startsWith("00")) d = d.slice(2);
  if (d.startsWith("07")) d = "964" + d.slice(1);
  return d;
};

export const PasswordResetDialog = ({ accountId, phone, customerId, onClose }: { accountId: string; phone?: string; customerId?: string; onClose: () => void }) => {
  const { t } = usePrefs();
  const [members, setMembers] = useState<Member[]>([]);
  const [uid, setUid] = useState(customerId || "");
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState<{ u: string; p: string } | null>(null);
  useEffect(() => {
    if (customerId) return;
    api.get(`/admin/registry/partners/${accountId}/members`).then((r) => {
      setMembers(r.data);
      setUid((r.data.find((m: Member) => m.venue_role === "owner") || r.data[0])?.user_id || "");
    }).catch((e) => toast.error(errorText(e, t)));
  }, [accountId, customerId, t]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/registry/users/${uid}/password`, { password: pw });
      setDone({ u: data.username || "", p: pw });
      toast.success(t("reg.resetPwDone"));
    } catch (err) {
      toast.error(errorText(err, t));
    } finally {
      setBusy(false);
    }
  };
  const msg = done ? t(customerId ? "reg.resetPwMsgCustomer" : "reg.resetPwMsg").replace("{site}", window.location.origin).replace("{u}", done.u).replace("{p}", done.p).replace(/\\n/g, "\n") : "";

  return (
    <Dialog open onOpenChange={(o: boolean) => !o && onClose()}>
      <DialogContent className="rounded-3xl max-w-[calc(100vw-2rem)] sm:max-w-md" data-testid="reset-password-dialog">
        <DialogHeader className="text-start"><DialogTitle className="font-display text-xl inline-flex items-center gap-2"><KeyRound className="h-5 w-5" />{t("reg.resetPwTitle")}</DialogTitle></DialogHeader>
        {done ? (
          <div className="space-y-3">
            <p className="text-sm font-semibold">{t(customerId ? "reg.resetPwDoneCustomer" : "reg.resetPwDone")}</p>
            <pre dir="auto" className="whitespace-pre-wrap font-sans rounded-2xl bg-muted p-3 text-sm leading-relaxed">{msg}</pre>
            {phone && <a href={`https://wa.me/${waNum(phone)}?text=${encodeURIComponent(msg)}`} target="_blank" rel="noreferrer" data-testid="reset-password-whatsapp"
              className="h-11 w-full rounded-full bg-[#25D366] text-white font-bold inline-flex items-center justify-center gap-2"><MessageCircle className="h-4 w-4" />{t("reg.resetPwSend")}</a>}
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-3">
            {!customerId && <div className="space-y-1.5">
              <Label htmlFor="rp-user">{t("reg.resetPwUser")}</Label>
              <select id="rp-user" value={uid} onChange={(e) => setUid(e.target.value)} data-testid="reset-password-user" className="h-11 w-full rounded-2xl border bg-background px-3 text-sm">
                {members.map((m) => <option key={m.user_id} value={m.user_id}>{m.name} · @{m.username} · {t(`role.${m.venue_role}`)}</option>)}
              </select>
            </div>}
            <div className="space-y-1.5">
              <Label htmlFor="rp-pw">{t("reg.resetPwNew")}</Label>
              <Input id="rp-pw" dir="ltr" required minLength={8} maxLength={128} value={pw} onChange={(e: any) => setPw(e.target.value)} data-testid="reset-password-input" className="h-11 rounded-2xl" />
            </div>
            <button disabled={busy || !uid || pw.length < 8} data-testid="reset-password-submit" className="h-11 w-full rounded-full bg-primary text-primary-foreground font-bold disabled:opacity-60">{t("reg.resetPw")}</button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};
